<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-15
-->

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useFetch, useSeoMeta, useI18n } from '#imports';
import type { Course, SubCourse } from '~/types/courses';

const { t } = useI18n();
const { trackCourseFlip, trackCourseFilter } = useAppTracking();

// 1. Fetch courses list from our Nitro BFF proxy
const { data: coursesData, pending, error } = await useFetch<{ data: Course[] }>('/api/courses/zoom');

// 2. Client-side Search and Filter states
const searchQuery = ref('');
const selectedSubject = ref('all');
const selectedGrade = ref('all');
const flippedCards = ref<Record<string, boolean>>({});

const toggleCardFlip = (courseId: string) => {
  const isCurrentlyFlipped = !!flippedCards.value[courseId];
  flippedCards.value = {};
  if (!isCurrentlyFlipped) {
    flippedCards.value[courseId] = true;

    // Track course card flip event
    const course = coursesList.value.find((c) => c._id === courseId);
    if (course) {
      trackCourseFlip(courseId, course.title);
    }
  }
};

const beforeLeave = (el: Element) => {
  const htmlEl = el as HTMLElement;
  htmlEl.style.left = `${htmlEl.offsetLeft}px`;
  htmlEl.style.top = `${htmlEl.offsetTop}px`;
  htmlEl.style.width = `${htmlEl.offsetWidth}px`;
  htmlEl.style.height = `${htmlEl.offsetHeight}px`;
};

// Extract the courses list from the pagination result wrapper { data: [...] }
const coursesList = computed(() => {
  return coursesData.value?.data || [];
});

// Normalization helper to prevent mismatch from whitespace or syntax differences
const normalizeName = (name: string) => {
  if (!name) return '';
  return name.replace(/\s+/g, '').toLowerCase().trim();
};

// Dynamic filter logic with normalization
const filteredCourses = computed(() => {
  return coursesList.value.filter((course: Course) => {
    // 1. Filter out courses with yearly price <= 0
    const yearlyPrice = course.price?.year ?? 0;
    if (yearlyPrice <= 0) return false;

    // 2. Search filter (handles course title, teacher name, or sub-course teacher/title)
    const matchesSearch =
      !searchQuery.value ||
      course.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      (course.teacher?.name && course.teacher.name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
      (Array.isArray(course.courses) &&
        course.courses.some(
          (sub: SubCourse) =>
            (sub.title && sub.title.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
            (sub.teacher?.name && sub.teacher.name.toLowerCase().includes(searchQuery.value.toLowerCase()))
        ));

    // 3. Subject filter (checks own subject OR any sub-course subject)
    const matchesSubject =
      selectedSubject.value === 'all' ||
      (course.subject?.name && normalizeName(course.subject.name) === normalizeName(selectedSubject.value)) ||
      (Array.isArray(course.courses) &&
        course.courses.some(
          (sub: SubCourse) =>
            sub.subject?.name && normalizeName(sub.subject.name) === normalizeName(selectedSubject.value)
        ));

    // 4. Grade filter (compares normalized grade names)
    const matchesGrade =
      selectedGrade.value === 'all' ||
      (course.grade?.name && normalizeName(course.grade.name) === normalizeName(selectedGrade.value));

    return matchesSearch && matchesSubject && matchesGrade;
  });
});

// Watch and track filter events
watch(selectedSubject, (newValue) => {
  if (newValue && newValue !== 'all') {
    trackCourseFilter('subject', newValue);
  }
});

watch(selectedGrade, (newValue) => {
  if (newValue && newValue !== 'all') {
    trackCourseFilter('grade', newValue);
  }
});

// Watch and track search queries with a 1-second debounce
let searchDebounceTimeout: NodeJS.Timeout | null = null;
watch(searchQuery, (newValue) => {
  if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);

  searchDebounceTimeout = setTimeout(() => {
    if (newValue.trim()) {
      trackCourseFilter('search', newValue.trim());
    }
  }, 1000);
});

// Set SEO metadata for the public catalog page
useSeoMeta({
  title: () => t('courses.seoTitle'),
  description: () => t('courses.seoDesc'),
  ogTitle: () => t('courses.seoTitle'),
  ogDescription: () => t('courses.seoDesc'),
  ogImage: '/img/courses.png',
  twitterCard: 'summary_large_image',
});
</script>

<template>
  <div
    class="flex min-h-screen flex-col bg-slate-50 font-sans text-slate-800 selection:bg-brand-red selection:text-white"
  >
    <!-- Header -->
    <HomeHeader />

    <!-- Header Spacer to prevent layout shift with fixed header -->
    <div class="h-18 w-full shrink-0 md:h-31"></div>

    <!-- Gradient Hero Banner -->
    <CoursesHero />

    <!-- Main Content Area -->
    <main class="mx-auto w-full max-w-360 flex-1 px-6 py-8 md:px-20 md:py-12">
      <!-- Search & Filters Bar Container -->
      <CoursesFilterBar
        v-model:search-query="searchQuery"
        v-model:selected-grade="selectedGrade"
        v-model:selected-subject="selectedSubject"
      />

      <!-- Main Content Area with transition wrapper -->
      <div class="relative min-h-[45vh]">
        <Transition name="fade-slow" mode="out-in">
          <!-- Loading State -->
          <div
            v-if="pending"
            key="loading"
            class="relative min-h-[40vh] rounded-3xl border border-gray-100 bg-white shadow-xs"
          >
            <LogoLoader size="h-16 w-16" :text="t('courses.loading')" overlay-class="bg-white/90" />
          </div>

          <!-- Error State -->
          <CoursesErrorState v-else-if="error" key="error" />

          <!-- Catalog Container -->
          <div v-else key="content">
            <Transition name="fade-slow" mode="out-in">
              <!-- Empty State -->
              <CoursesEmptyState v-if="filteredCourses.length === 0" key="empty" />

              <!-- Grid -->
              <div v-else key="grid">
                <TransitionGroup
                  appear
                  tag="div"
                  name="course-grid"
                  class="relative grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                  @before-leave="beforeLeave"
                >
                  <CoursesCard
                    v-for="(course, index) in filteredCourses"
                    :key="course._id"
                    :course="course"
                    :is-flipped="!!flippedCards[course._id]"
                    :style="{ '--stagger-delay': Math.min(index, 8) }"
                    @toggle-flip="toggleCardFlip(course._id)"
                  />
                </TransitionGroup>
              </div>
            </Transition>
          </div>
        </Transition>
      </div>
    </main>

    <!-- Footer -->
    <HomeFooter />
  </div>
</template>

<style scoped>
.font-sans {
  font-family:
    'Kantumruy Pro',
    'Kantumruy',
    'Ubuntu',
    system-ui,
    -apple-system,
    sans-serif;
}
</style>
